import { FormItem, Input, InputProps } from '@billy/design-system'
import { ChangeEvent, ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EntityType } from '../../enums/entityType'
import { EInvoiceScheme } from '../../types/eInvoiceScheme'
import { EInvoiceAddressInputLabel } from './elements/EInvoiceAddressInputLabel'
import { getEInvoiceAddressPlaceholder } from './utils/getEInvoiceAddressPlaceholder'
import { getIsUnsupportedEInvoiceAddressType } from './utils/getIsUnsupportedEInvoiceAddressType'

interface EInvoiceAddressInputFieldProps {
  address?: string
  autoCompleted?: boolean
  countryId?: string
  eInvoiceAddressFormField?: UseFormRegisterReturn
  eInvoiceAddressInputErrorMessage?: string
  entityType?: EntityType
  formItemColumnEnd?: number
  formItemColumnStart?: number
  inputLabel?: ReactNode
  inputPlaceholder?: string
  onAddressChange: (address?: string) => void
  schemeType: EInvoiceScheme
  shouldDisplayInputFieldLabelTooltip: boolean
  size?: InputProps['size']
}

export const EInvoiceAddressInputField = ({
  address,
  autoCompleted,
  countryId,
  eInvoiceAddressFormField,
  eInvoiceAddressInputErrorMessage,
  entityType,
  formItemColumnEnd,
  formItemColumnStart,
  inputLabel,
  inputPlaceholder,
  onAddressChange,
  schemeType,
  shouldDisplayInputFieldLabelTooltip,
  size,
}: EInvoiceAddressInputFieldProps): ReactElement => {
  const { t } = useTranslation()

  const isUnsupportedEInvoiceAddressType = useMemo(
    () => getIsUnsupportedEInvoiceAddressType(entityType, schemeType),
    [entityType, schemeType],
  )

  const handleAddressChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onAddressChange(event.target.value)
    },
    [onAddressChange],
  )

  return (
    <FormItem
      error={eInvoiceAddressInputErrorMessage}
      itemColumnEnd={formItemColumnEnd}
      itemColumnStart={formItemColumnStart}
      label={
        inputLabel ?? (
          <EInvoiceAddressInputLabel
            countryId={countryId}
            entityType={entityType}
            schemeType={schemeType}
            shouldDisplayInputFieldLabelTooltip={shouldDisplayInputFieldLabelTooltip}
          />
        )
      }
    >
      <Input
        autoCompleted={autoCompleted}
        disabled={isUnsupportedEInvoiceAddressType}
        onChange={handleAddressChange}
        placeholder={inputPlaceholder ?? getEInvoiceAddressPlaceholder(t, schemeType, isUnsupportedEInvoiceAddressType)}
        ref={eInvoiceAddressFormField?.ref}
        size={size}
        value={address}
      />
    </FormItem>
  )
}
